/**
 * features/diagram/NodeView — render one diagram node (shape + centered text)
 * and wire the full Lucid-style interaction set:
 *   • click → select (shift toggles); drag → move ALL selected nodes together,
 *     snapping to the grid when appStore.snapOn;
 *   • hover → connection ports appear INSIDE this group (so they can never
 *     steal pointerdown from other nodes — the old separate ports layer
 *     covered sibling nodes and broke dragging);
 *   • double-click → inline text edit.
 *
 * Thin-React: screen→content conversion uses the SHARED camera via the
 * editorStore content ref; math lives in editor-core. Drag mutates only x/y
 * through moveNode so connected edges re-route on render ("sticky").
 */
import { useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from "react";
import { screenToContent } from "../../editor-core";
import { cycleCss, NODE_ANIM_CYCLE_MS, freedrawPath } from "../../editor-core/diagram";
import type { DiagramNode } from "../../editor-core/diagram";
import { useEditorStore } from "../../state/editorStore";
import { useDiagramStore } from "../../state/diagramStore";
import { useAppStore } from "../../state/appStore";
import { panState } from "../../state/panState";
import { beginNodeTextEdit } from "./nodeTextEdit";
import { LABEL_INK, nodeFontStack } from "./typography";
import { ConnectionPorts, type PreviewEdge } from "./ConnectionPorts";
import { shapeElement } from "./ShapePalette";
import { labelLines } from "./textWrap";

const SELECT_STROKE = "#6965db"; // Excalidraw selection violet
/** Snap increment — half of the 22px background grid. */
const SNAP = 11;
/** Forgiving hover halo around the node so ports are easy to reach. */
// Must exceed the connection band's outer reach (BAND/2 ≈ 11) so the pointer
// staying on the band keeps the node "hovered" — otherwise ConnectionPorts
// unmounts as the cursor nears the border and the follow-dot can't be grabbed.
const HALO = 20;

// shapeElement (all kinds + icon glyphs) lives in ./ShapePalette — the single
// source of truth shared by the canvas render and the palette mini-previews.

export function NodeView({
  node,
  onPreviewChange,
}: {
  node: DiagramNode;
  /** Bubble the drag-to-connect preview up to DiagramLayer for rendering. */
  onPreviewChange: (p: PreviewEdge | null) => void;
}) {
  // Per-shape selection chrome (outline + grips) only for a SOLO selection;
  // a multi-selection / group gets ONE SelectionFrame instead (Excalidraw).
  const inMulti = useDiagramStore((s) => s.diagramSelection.length > 1 && s.diagramSelection.includes(node.id));
  const selected = useDiagramStore((s) =>
    s.diagramSelection.length === 1 && s.diagramSelection[0] === node.id,
  );
  // Connect affordance (ports + border band) exists ONLY in arrow mode —
  // select mode is pure move/resize.
  const arrowMode = useEditorStore((s) => s.tool === "arrow");
  const connectMode = arrowMode;
  // Hand cursor: open hand (grab) over a shape, closed hand (grabbing) while
  // dragging it — the Lucid/Figma affordance for "this is draggable".
  const dragging = useDiagramStore((s) => s.draggingId === node.id);
  const [hovered, setHovered] = useState(false);

  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    if (panState.spaceHeld) return; // Space-pan owns the gesture — hand drags the page
    // An armed draw/text tool draws/types here instead (Canvas owns it).
    const t = useEditorStore.getState().tool;
    if (t === "draw" || t === "text" || t === "pen" || t === "eraser" || t === "laser") return;
    e.stopPropagation();
    const d = useDiagramStore.getState();
    // Keep both nodes AND edges — mixed selections group/delete together;
    // the drag below only moves the node ids among them.
    let sel = d.diagramSelection.filter((id) => d.nodes[id] || d.edges[id]);
    // A grouped node selects/deselects its whole group (⌘G unit);
    // ⌘/Ctrl-click DEEP-selects just this member (Figma), to edit or move it
    // alone without ungrouping.
    const gid = d.nodes[node.id]?.groupId;
    const deep = !!gid && (e.metaKey || e.ctrlKey) && !e.shiftKey;
    const members = gid && !deep
      ? Object.values(d.nodes).filter((n) => n.groupId === gid).map((n) => n.id)
      : [node.id];
    if (deep) {
      sel = [node.id];
      d.setDiagramSelection(sel);
    } else if (e.shiftKey) {
      // Toggle within the FULL selection — selected arrows must survive a
      // shift-click on a shape (multi-select of shapes + arrows).
      const full = d.diagramSelection;
      const next = full.includes(node.id)
        ? full.filter((id) => !members.includes(id))
        : [...full, ...members.filter((id) => !full.includes(id))];
      d.setDiagramSelection(next);
      sel = next.filter((id) => d.nodes[id]);
      if (!sel.includes(node.id)) return; // toggled off → no drag
    } else if (!sel.includes(node.id)) {
      sel = members;
      d.setDiagramSelection(sel);
    }

    const refs = useEditorStore.getState().refs;
    if (!refs) return;
    const content = refs.content;
    // Mark this node as locally dragging so remote collab merges don't yank it.
    d.setDraggingId(node.id);
    const start = screenToContent(content, e.clientX, e.clientY);
    // Snapshot original positions so snapping never accumulates drift.
    const origs = sel
      .map((id) => {
        const n = useDiagramStore.getState().nodes[id];
        return n ? { id, x: n.x, y: n.y } : null;
      })
      .filter(Boolean) as { id: string; x: number; y: number }[];

    // Smart alignment (Excalidraw/Figma): the moving selection's centre and
    // edges snap to other shapes' centres/edges within a few SCREEN px, with a
    // guide line — so "almost lined up" boxes (and the arrows between them)
    // become exactly straight. Alignment beats the grid on that axis.
    const sizes = new Map(origs.map((o) => {
      const n = useDiagramStore.getState().nodes[o.id];
      return [o.id, { w: n?.w ?? 0, h: n?.h ?? 0 }];
    }));
    const box0 = origs.reduce(
      (b, o) => {
        const z = sizes.get(o.id)!;
        return { x0: Math.min(b.x0, o.x), y0: Math.min(b.y0, o.y), x1: Math.max(b.x1, o.x + z.w), y1: Math.max(b.y1, o.y + z.h) };
      },
      { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity },
    );
    const moving = new Set(origs.map((o) => o.id));
    const targets = Object.values(useDiagramStore.getState().nodes).filter(
      (n) => !moving.has(n.id) && n.kind !== "freedraw" && n.w > 0 && n.h > 0,
    );
    const tx = targets.flatMap((n) => [n.x, n.x + n.w / 2, n.x + n.w]);
    const ty = targets.flatMap((n) => [n.y, n.y + n.h / 2, n.y + n.h]);
    const best = (refs: number[], lines: number[], tol: number): { d: number; at: number } | null => {
      let out: { d: number; at: number } | null = null;
      for (const r of refs) for (const l of lines) {
        const d = l - r;
        if (Math.abs(d) <= tol && (!out || Math.abs(d) < Math.abs(out.d))) out = { d, at: l };
      }
      return out;
    };

    const move = (ev: PointerEvent) => {
      const p = screenToContent(content, ev.clientX, ev.clientY);
      let dx = p.x - start.x;
      let dy = p.y - start.y;
      const snap = useAppStore.getState().snapOn;
      const store = useDiagramStore.getState();
      const tol = 6 / (useEditorStore.getState().cam.z || 1);
      const bx = { x0: box0.x0 + dx, x1: box0.x1 + dx, y0: box0.y0 + dy, y1: box0.y1 + dy };
      const ax = ev.altKey ? null : best([bx.x0, (bx.x0 + bx.x1) / 2, bx.x1], tx, tol);
      const ay = ev.altKey ? null : best([bx.y0, (bx.y0 + bx.y1) / 2, bx.y1], ty, tol);
      if (ax) dx += ax.d;
      if (ay) dy += ay.d;
      useEditorStore.getState().setAlignGuides({ x: ax ? [ax.at] : [], y: ay ? [ay.at] : [] });
      for (const o of origs) {
        let nx = o.x + dx;
        let ny = o.y + dy;
        if (snap) {
          if (!ax) nx = Math.round(nx / SNAP) * SNAP;
          if (!ay) ny = Math.round(ny / SNAP) * SNAP;
        }
        const cur = store.nodes[o.id];
        if (cur && (cur.x !== nx || cur.y !== ny)) {
          store.moveNode(o.id, nx - cur.x, ny - cur.y);
        }
      }
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      useEditorStore.getState().setAlignGuides({ x: [], y: [] });
      useDiagramStore.getState().setDraggingId(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const onDoubleClick = (e: ReactMouseEvent) => {
    e.stopPropagation();
    beginNodeTextEdit(node);
  };

  const cx = node.x + node.w / 2;
  const cy = node.y + node.h / 2;
  // Label formatting (all optional → default look). Alignment shifts both the
  // anchor point AND the text-anchor; a small pad keeps left/right text off the
  // border. Every field is rendered as an INLINE SVG attribute so it survives
  // the DOM-clone bake in editorStore.currentBoardSvg (CSS classes do NOT).
  const align = node.textAlign ?? "center";
  const TEXT_PAD = 8;
  const tx = align === "left" ? node.x + TEXT_PAD : align === "right" ? node.x + node.w - TEXT_PAD : cx;
  const textAnchor = align === "left" ? "start" : align === "right" ? "end" : "middle";
  // Multi-line block stays vertically centered on the label anchor: shift the
  // first line up by half the extra lines' height. Icons are SINGLE shapes
  // (Lucid): the caption hangs BELOW the icon box instead.
  const lines = labelLines(node);
  const lineHeight = (node.fontSize ?? 14) * 1.25;
  const firstLineY =
    node.kind === "icon"
      ? node.y + node.h + 6 + lineHeight / 2
      : cy - ((lines.length - 1) * lineHeight) / 2;
  // Node-level opacity (absent/1 → omitted so the attribute never bloats saves).
  const nodeOpacity = node.opacity != null && node.opacity !== 1 ? node.opacity : undefined;
  // Eraser sweep: marked objects fade until the pointer is released.
  const erasing = useEditorStore((s) => s.eraseMarked.includes(node.id));
  // Glow halo takes the node's stroke color; transparent strokes fall back
  // to the accent so the effect is never invisible.
  const glowColor =
    node.stroke && node.stroke !== "none" && node.stroke !== "transparent"
      ? node.stroke
      : "#2563eb";

  return (
    <g
      data-diagram-node={node.id}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      style={{ cursor: dragging ? "grabbing" : "grab" }}
    >
      {/* forgiving hover halo (transparent but hit-testable) — ARROW mode
          only, where it keeps the border ports reachable. In select mode it
          would swallow a marquee started next to a shape (worst on borderless
          text labels) and grab the shape instead. */}
      {arrowMode && (
        <rect
          data-editor-only="1"
          x={node.x - HALO}
          y={node.y - HALO}
          width={node.w + HALO * 2}
          height={node.h + HALO * 2}
          fill="transparent"
          stroke="none"
        />
      )}
      {/* Idle animation wraps shape+text so they move as one; the halo,
          selection box and ports stay OUTSIDE the group so editor chrome
          never pulses/wobbles. transform-box/origin come from the CSS class;
          speed is inline (beats the class default); data-* attrs let the GIF
          exporter bake the exact same motion deterministically. */}
      <g
        opacity={erasing ? 0.25 : nodeOpacity}
        // Rotation wraps shape+text; the selection box, grips and rotate
        // handle carry the SAME transform (drawn outside this group so the
        // idle animation never wobbles them). Halo and ports stay
        // axis-aligned — the bbox model the router/ports use.
        transform={node.rotation ? `rotate(${node.rotation} ${cx} ${cy})` : undefined}
        className={node.anim ? `node-anim node-anim-${node.anim}` : undefined}
        data-node-anim={node.anim ?? undefined}
        data-anim-speed={node.anim ? node.animSpeed ?? 1 : undefined}
        data-anim-cx={node.anim ? cx : undefined}
        data-anim-cy={node.anim ? cy : undefined}
        data-anim-color={node.anim === "glow" ? glowColor : undefined}
        style={
          node.anim
            ? ({
                animationDuration: cycleCss(NODE_ANIM_CYCLE_MS, node.animSpeed ?? 1),
                "--glow-color": glowColor,
              } as CSSProperties)
            : undefined
        }
      >
        {/* Hand-drawn look: a shared roughen filter jitters the shape edges;
            the text switches to a handwriting font. Filter id is global +
            idempotent (see SketchDefs, rendered once by DiagramLayer). */}
        <g filter={node.sketch && node.kind !== "icon" ? "url(#noddle-sketch)" : undefined}>
          {shapeElement(node)}
        </g>
        <text
          x={node.kind === "icon" ? cx : tx}
          y={firstLineY}
          textAnchor={node.kind === "icon" ? "middle" : textAnchor}
          dominantBaseline="central"
          fontSize={node.fontSize ?? 14}
          // Icon captions default to semibold (thin 400 read weak under a
          // saturated tile); an explicit bold/normal pick still wins.
          fontWeight={node.bold ? 700 : node.kind === "icon" && node.bold === undefined ? 600 : undefined}
          fontStyle={node.italic ? "italic" : undefined}
          textDecoration={node.underline ? "underline" : undefined}
          fill={node.textColor ?? LABEL_INK}
          fontFamily={nodeFontStack(node)}
          letterSpacing={node.letterSpacing || undefined}
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {/* Multi-line labels: \n from the editor + auto word-wrap when
              node.wrap. tspans (not CSS) so the DOM-clone bake keeps them. */}
          {lines.map((line, i) => (
            <tspan
              key={i}
              x={node.kind === "icon" ? cx : tx}
              dy={i === 0 ? 0 : lineHeight}
            >
              {line || " "}
            </tspan>
          ))}
        </text>
      </g>
      {/* Connect affordance FIRST (below the selection chrome) — its full-node
          overlay must not sit on top of the resize grips or it steals their
          pointerdown and the shape can't be scaled. */}
      {connectMode && (hovered || selected) && (
        <ConnectionPorts node={node} selected={selected} onPreviewChange={onPreviewChange} />
      )}
      {/* Part of a MULTI-selection: a faint outline so you can tell which
          shapes are in it (grips belong to the single SelectionFrame). */}
      {/* pen strokes: the selection HUGS the ink (no box) — solo or multi */}
      {(inMulti || selected) && !arrowMode && node.kind === "freedraw" && (
        <path
          data-editor-only="1"
          d={freedrawPath(node.points, node.x, node.y, node.w, node.h)}
          transform={chromeRotate(node)}
          fill="none"
          stroke={SELECT_STROKE}
          strokeOpacity={selected ? 0.45 : 0.32}
          strokeWidth={(node.pen?.type === "highlighter" ? node.strokeWidth * 3 : node.strokeWidth) + 7}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pointerEvents: "none" }}
        />
      )}
      {inMulti && !arrowMode && node.kind !== "freedraw" && (
        <rect
          data-editor-only="1"
          x={node.x - 4}
          y={node.y - 4}
          width={node.w + 8}
          height={node.h + 8}
          transform={chromeRotate(node)}
          rx={4}
          fill="none"
          stroke={SELECT_STROKE}
          strokeOpacity={0.55}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
          style={{ pointerEvents: "none" }}
        />
      )}
      {/* Excalidraw selection: a thin SOLID outline + 4 corner grips. The arrow
          tool shows none of it (its only cue is the glow). */}
      {selected && !arrowMode && node.kind !== "freedraw" && (
        <rect
          data-editor-only="1"
          x={node.x - 5}
          y={node.y - 5}
          width={node.w + 10}
          height={node.h + 10}
          transform={chromeRotate(node)}
          fill="none"
          stroke={SELECT_STROKE}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
          style={{ pointerEvents: "none" }}
        />
      )}
      {/* Resize grips LAST so they sit above the connect overlay. */}
      {selected && !arrowMode && <ResizeHandles node={node} />}
    </g>
  );
}

/**
 * Eight resize grips (corners + edge midpoints) on the selected node — the
 * on-canvas "scale up/down" affordance that was missing (users could only resize
 * via the Properties W/H fields). Grips keep a constant SCREEN size by dividing
 * by the camera zoom, so they stay grabbable at any zoom. Dragging mutates
 * x/y/w/h through updateNode (min 20); connected edges re-route automatically.
 */
/**
 * Selection chrome (dashed box, resize grips, rotate zones/button) follows the
 * node's rotation so it hugs the shape the user actually sees — same transform
 * as the shape group: degrees clockwise around the bbox center.
 */
function chromeRotate(node: DiagramNode): string | undefined {
  return node.rotation
    ? `rotate(${node.rotation} ${node.x + node.w / 2} ${node.y + node.h / 2})`
    : undefined;
}

interface HandleRole {
  id: string;
  fx: number;
  fy: number;
  L?: boolean;
  R?: boolean;
  T?: boolean;
  B?: boolean;
  cur: string;
}
const HANDLE_ROLES: HandleRole[] = [
  { id: "nw", fx: 0, fy: 0, L: true, T: true, cur: "nwse-resize" },
  { id: "n", fx: 0.5, fy: 0, T: true, cur: "ns-resize" },
  { id: "ne", fx: 1, fy: 0, R: true, T: true, cur: "nesw-resize" },
  { id: "e", fx: 1, fy: 0.5, R: true, cur: "ew-resize" },
  { id: "se", fx: 1, fy: 1, R: true, B: true, cur: "nwse-resize" },
  { id: "s", fx: 0.5, fy: 1, B: true, cur: "ns-resize" },
  { id: "sw", fx: 0, fy: 1, L: true, B: true, cur: "nesw-resize" },
  { id: "w", fx: 0, fy: 0.5, L: true, cur: "ew-resize" },
];

/**
 * Rotation affordance (draw.io-style): hovering just OUTSIDE any corner of
 * the selected node shows a rotate cursor — drag there to rotate around the
 * center. A small visible ↻ button floats past the top-right corner for
 * discoverability (double-click it to reset to 0°). With snap on (or Shift)
 * the angle sticks to 15° steps; 0/90/180/270 always attract within ±3°.
 * Rendered by DiagramLayer in the TOP overlay (z-order — see there).
 */
const ROTATE_CURSOR = `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18'>" +
    "<path d='M9 3a6 6 0 1 1-5.6 3.8' fill='none' stroke='white' stroke-width='4'/>" +
    "<path d='M9 3a6 6 0 1 1-5.6 3.8' fill='none' stroke='black' stroke-width='1.8'/>" +
    "<path d='M9 0.8 L12 3 L9 5.2 Z' fill='black' stroke='white' stroke-width='0.8'/>" +
    "</svg>",
)}") 9 9, grab`;

export function RotateHandle({ node }: { node: DiagramNode }) {
  const z = useEditorStore((s) => s.cam.z) || 1;
  const cx = node.x + node.w / 2;
  const cy = node.y + node.h / 2;

  const startRotate = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    if (panState.spaceHeld) return;
    e.stopPropagation();
    const refs = useEditorStore.getState().refs;
    if (!refs) return;
    const content = refs.content;
    const d = useDiagramStore.getState();
    d.setDraggingId(node.id); // collab merges must not yank mid-gesture
    const p0 = screenToContent(content, e.clientX, e.clientY);
    const startPointer = (Math.atan2(p0.y - cy, p0.x - cx) * 180) / Math.PI;
    const startRotation = node.rotation ?? 0;

    const move = (ev: PointerEvent) => {
      const p = screenToContent(content, ev.clientX, ev.clientY);
      const pointer = (Math.atan2(p.y - cy, p.x - cx) * 180) / Math.PI;
      let deg = startRotation + (pointer - startPointer);
      if (useAppStore.getState().snapOn || ev.shiftKey) {
        deg = Math.round(deg / 15) * 15;
      } else {
        // The cardinal angles always attract — a straight shape is one flick away.
        const near = Math.round(deg / 90) * 90;
        if (Math.abs(deg - near) <= 3) deg = near;
      }
      deg = ((deg % 360) + 360) % 360;
      useDiagramStore.getState().updateNode(node.id, {
        rotation: deg === 0 ? undefined : Math.round(deg * 10) / 10,
      });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      useDiagramStore.getState().setDraggingId(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // Invisible rotate zones just OUTSIDE each corner (past the resize grips).
  const zoneR = 11 / z;
  const off = 13 / z;
  const corners = [
    { x: node.x - off, y: node.y - off },
    { x: node.x + node.w + off, y: node.y - off },
    { x: node.x + node.w + off, y: node.y + node.h + off },
    { x: node.x - off, y: node.y + node.h + off },
  ];
  // Visible ↻ button floating past the top-right corner (draw.io-style).
  const btnR = 9 / z;
  const bx = node.x + node.w + 24 / z;
  const by = node.y - 24 / z;

  return (
    <g data-editor-only="1" transform={chromeRotate(node)}>
      {corners.map((c, i) => (
        <circle
          key={i}
          data-handle="rotate"
          cx={c.x}
          cy={c.y}
          r={zoneR}
          fill="transparent"
          style={{ cursor: ROTATE_CURSOR }}
          onPointerDown={startRotate}
        />
      ))}
      <g
        data-handle="rotate"
        style={{ cursor: ROTATE_CURSOR }}
        onPointerDown={startRotate}
        onDoubleClick={(e) => {
          e.stopPropagation();
          useDiagramStore.getState().updateNode(node.id, { rotation: undefined });
        }}
      >
        <circle
          cx={bx}
          cy={by}
          r={btnR}
          fill="#fff"
          stroke={SELECT_STROKE}
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
        <text
          x={bx}
          y={by}
          // Counter-rotate: the button orbits the rotated frame but the
          // glyph itself stays upright.
          transform={node.rotation ? `rotate(${-node.rotation} ${bx} ${by})` : undefined}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={12 / z}
          fill={SELECT_STROKE}
          style={{ pointerEvents: "none", userSelect: "none" }}
        >
          {"\u21bb"}
        </text>
      </g>
    </g>
  );
}

function ResizeHandles({ node }: { node: DiagramNode }) {
  const z = useEditorStore((s) => s.cam.z) || 1;
  const s = 8 / z; // constant ~8px screen handle
  const min = 20;

  const startResize =
    (role: HandleRole) => (e: ReactPointerEvent) => {
      if (e.button !== 0) return;
      if (panState.spaceHeld) return; // hand-pan wins over resizing
      e.stopPropagation();
      const refs = useEditorStore.getState().refs;
      if (!refs) return;
      const content = refs.content;
      const start = screenToContent(content, e.clientX, e.clientY);
      const o = { x: node.x, y: node.y, w: node.w, h: node.h };
      // Rotated node: the grips live on the ROTATED frame, so resize in the
      // node's local axes — inverse-rotate the pointer delta, apply the
      // axis-aligned math, then re-anchor the center so the grabbed grip
      // tracks the pointer and the opposite edge stays put on screen.
      const th = ((node.rotation ?? 0) * Math.PI) / 180;
      const cos = Math.cos(th);
      const sin = Math.sin(th);
      const d = useDiagramStore.getState();
      d.setDraggingId(node.id); // guard against collab merge yanking it

      const move = (ev: PointerEvent) => {
        const p = screenToContent(content, ev.clientX, ev.clientY);
        const wdx = p.x - start.x;
        const wdy = p.y - start.y;
        const dx = th ? wdx * cos + wdy * sin : wdx;
        const dy = th ? -wdx * sin + wdy * cos : wdy;
        let { x, y, w, h } = o;
        if (role.L) { const right = o.x + o.w; w = Math.max(min, o.w - dx); x = right - w; }
        if (role.R) { w = Math.max(min, o.w + dx); }
        if (role.T) { const bot = o.y + o.h; h = Math.max(min, o.h - dy); y = bot - h; }
        if (role.B) { h = Math.max(min, o.h + dy); }
        if (th) {
          // The center moved in LOCAL coords; rotation happens around the
          // (new) center, so map that center displacement back to world.
          const mx = x + w / 2 - (o.x + o.w / 2);
          const my = y + h / 2 - (o.y + o.h / 2);
          x = o.x + o.w / 2 + mx * cos - my * sin - w / 2;
          y = o.y + o.h / 2 + mx * sin + my * cos - h / 2;
        }
        useDiagramStore.getState().updateNode(node.id, { x, y, w, h });
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        useDiagramStore.getState().setDraggingId(null);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    };

  return (
    <g data-editor-only="1" transform={chromeRotate(node)}>
      {HANDLE_ROLES.map((r) => {
        const hx = node.x + node.w * r.fx;
        const hy = node.y + node.h * r.fy;
        // Excalidraw shows only the 4 CORNER grips; the side grips stay as
        // invisible (wider) hit targets so edges still drag-resize.
        const side = r.fx === 0.5 || r.fy === 0.5;
        const hw = side ? (r.fx === 0.5 ? Math.max(s, node.w - s * 2) : s) : s;
        const hh = side ? (r.fy === 0.5 ? Math.max(s, node.h - s * 2) : s) : s;
        return (
          <rect
            key={r.id}
            data-handle={r.id}
            x={hx - hw / 2}
            y={hy - hh / 2}
            width={hw}
            height={hh}
            rx={side ? 0 : s * 0.25}
            fill={side ? "transparent" : "#fff"}
            stroke={side ? "none" : SELECT_STROKE}
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            style={{ cursor: r.cur }}
            onPointerDown={startResize(r)}
          >
            <title>Drag to resize</title>
          </rect>
        );
      })}
    </g>
  );
}
